import { useEffect, useMemo, useState } from 'react'
import { apiService, type UkgAgentGraphResponse } from '@/service/api'
import type { MockEdge, MockNode } from './types'

export interface KnowledgeGraphData {
  nodes: MockNode[]
  edges: MockEdge[]
}

interface State {
  data: KnowledgeGraphData
  loading: boolean
  error: string | null
  empty: boolean
}

const ROOT_ID = 'tenant-root'

/**
 * Name for a sheet's uploader when the endpoint carries none. The backend
 * resolves `uploader_name` through Keycloak and answers null when that lookup
 * fails — which on an environment where it fails for everyone left Uploaded
 * by disabled. The Data Catalog names the same people from the organization
 * directory, so the page hands that in here and the filter works from either.
 */
export type ResolveUploader = (userId: string) => string | undefined

/**
 * The knowledge graph the AI answers from — the tenant's raw/ tables — as
 * nodes and edges for the canvas, legend, filters and details card:
 *
 *   tenant-root (enterprise)
 *     └─ sheet:<id>  (functional_location)  one per raw/sheet_meta row
 *          └─ equip:<tag>  (components)     one per raw/equipment_meta row,
 *                                            parented to its home sheet
 *
 * plus two kinds of non-tree edge: "appears on" from every OTHER sheet an
 * equipment is drawn on (raw/equipment_sheet), and the walked tag→tag
 * connectivity between two equipment (raw/connectivity), labelled with the
 * line it runs along. A connection to a tag that is not an equipment node
 * (an instrument, a line) is not drawn — the view is equipment-level.
 */
export const buildAgentGraph = (
  resp: UkgAgentGraphResponse,
  resolveUploader?: ResolveUploader,
  tenantName?: string,
): KnowledgeGraphData => {
  const nodes: MockNode[] = []
  const edges: MockEdge[] = []
  let edgeSeq = 0

  nodes.push({
    id: ROOT_ID,
    // The root IS the tenant, so it says the tenant's name. It used to read
    // "Agent Knowledge (raw/)" — the name of the STORE the graph is built
    // from, which named our plumbing rather than the reader's company. The
    // workspace stays below as an attribute, where the storage detail belongs.
    label: tenantName?.trim() || resp.workspace || 'Knowledge Graph',
    type: 'enterprise',
    attributes: [
      { label: 'Workspace', value: resp.workspace ?? '—' },
      { label: 'Sheets', value: String(resp.sheets.length) },
      { label: 'Equipment', value: String(resp.equipment.length) },
      { label: 'Connections', value: String(resp.connections.length) },
      { label: 'Sample', value: resp.truncated ? 'Yes — row caps hit' : 'No' },
    ],
  })

  const sheetNodeIds = new Set<string>()
  for (const sheet of resp.sheets) {
    const id = `sheet:${sheet.sheet}`
    sheetNodeIds.add(id)
    const uploadedBy = sheet.uploader_name ?? (sheet.uploader_id ? resolveUploader?.(sheet.uploader_id) : undefined)
    nodes.push({
      id,
      label: sheet.drawing_number ?? sheet.sheet,
      type: 'functional_location',
      parent: ROOT_ID,
      attributes: [
        { label: 'Sheet ID', value: sheet.sheet },
        { label: 'Sheet #', value: sheet.sheet_number ?? '—' },
        { label: 'Drawing #', value: sheet.drawing_number ?? '—' },
        { label: 'Drawing type', value: sheet.drawing_type ?? '—' },
        { label: 'System', value: sheet.system ?? '—' },
        { label: 'Area', value: sheet.area_of_plant ?? '—' },
        { label: 'Symbols parsed', value: String(sheet.symbol_count) },
        { label: 'Uploaded by', value: uploadedBy ?? '—' },
      ],
      // The drawing behind the sheet: its chip under Attached Documents, the
      // file Open Preview opens, and what Mapped Tag asks Tag Mapping about.
      attachedDocuments: [sheet.drawing_number ?? sheet.sheet],
      documentId: sheet.document_id ?? undefined,
      uploadedBy,
    })
    edges.push({ id: `e${edgeSeq++}`, source: ROOT_ID, target: id })
  }

  const equipNodeIds = new Set<string>()
  for (const eq of resp.equipment) {
    const id = `equip:${eq.tag}`
    if (equipNodeIds.has(id)) continue
    equipNodeIds.add(id)
    // Home sheet first (the backend orders it so); an equipment the parse
    // placed on no known sheet hangs off the root rather than vanishing.
    const onSheets = eq.sheets.map((s) => `sheet:${s}`).filter((s) => sheetNodeIds.has(s))
    const parent = onSheets[0] ?? ROOT_ID
    nodes.push({
      id,
      label: eq.tag,
      type: 'components',
      parent,
      attributes: [
        { label: 'Tag', value: eq.tag },
        { label: 'Name', value: eq.name ?? '—' },
        { label: 'Equipment type', value: eq.equipment_type ?? '—' },
        { label: 'Home sheet', value: eq.home_sheet ?? '—' },
        { label: 'Appears on', value: eq.sheets.length > 0 ? eq.sheets.join(', ') : '—' },
      ],
    })
    edges.push({ id: `e${edgeSeq++}`, source: parent, target: id })
    for (const other of onSheets.slice(1)) {
      edges.push({ id: `e${edgeSeq++}`, source: other, target: id, label: 'appears on' })
    }
  }

  // One line per equipment pair: connectivity is directed and both directions
  // are usually present, which would draw every link twice.
  const seenPairs = new Set<string>()
  for (const c of resp.connections) {
    const from = `equip:${c.src_tag}`
    const to = `equip:${c.dst_tag}`
    if (!equipNodeIds.has(from) || !equipNodeIds.has(to) || from === to) continue
    const key = from < to ? `${from}|${to}` : `${to}|${from}`
    if (seenPairs.has(key)) continue
    seenPairs.add(key)
    edges.push({ id: `e${edgeSeq++}`, source: from, target: to, label: c.line ?? c.direction ?? 'connected to' })
  }

  return { nodes, edges }
}

/**
 * The page's data: `GET /v2/ukg/agent-graph`, the raw/ parquet the agent
 * answers from. (The catalog hierarchy view that preceded it was retired —
 * product call, 2026-10-07.) `available: false` is not a fault in the page:
 * the pod holds no raw/ for the tenant, and the reason is shown as the error.
 */
export const useKnowledgeGraphData = (resolveUploader?: ResolveUploader, tenantName?: string): State => {
  const [state, setState] = useState<State>({
    data: { nodes: [], edges: [] },
    loading: true,
    error: null,
    empty: false,
  })
  // The response is kept so the graph can be rebuilt when the organization
  // directory arrives after it (the directory loads lazily).
  const [resp, setResp] = useState<UkgAgentGraphResponse | null>(null)

  useEffect(() => {
    let cancelled = false
    const load = async () => {
      try {
        const resp = await apiService.getUkgAgentGraph()
        if (cancelled) return
        if (!resp.available) {
          setState({
            data: { nodes: [], edges: [] },
            loading: false,
            error: resp.reason ?? 'The knowledge graph is not available on this server.',
            empty: false,
          })
          return
        }
        const empty = resp.sheets.length === 0 && resp.equipment.length === 0
        setResp(resp)
        setState({ data: { nodes: [], edges: [] }, loading: false, error: null, empty })
      } catch (err) {
        if (cancelled) return
        setState({
          data: { nodes: [], edges: [] },
          loading: false,
          error: err instanceof Error ? err.message : 'Failed to load knowledge graph',
          empty: false,
        })
      }
    }
    void load()
    return () => {
      cancelled = true
    }
  }, [])

  const data = useMemo(
    () => (resp ? buildAgentGraph(resp, resolveUploader, tenantName) : state.data),
    [resp, resolveUploader, tenantName, state.data],
  )

  return resp ? { ...state, data } : state
}
